import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, ExternalLink, Globe, Receipt, Server, Lock, Calendar,
  CheckCircle2, PauseCircle, AlertTriangle, Mail, Loader2,
  Eye, EyeOff, Copy, KeyRound, User as UserIcon, X,
} from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import {
  customerListHosting, customerListInvoices, customerSendWelcome,  customerGetCredentials, customerChangeStackPassword,
  customerRenewOptions, customerRequestRenewal, customerRenewCreateOrder, customerRenewVerify,
  customerPendingPayLinks,
  customerInvoicePayCreateOrder, customerInvoicePayVerify,
} from "../lib/api";
import { trackBeginCheckout, trackPurchase } from "../lib/tracking";


function StatusPill({ status }) {
  const map = {
    active: { label: "Active", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
    suspended: { label: "Suspended", cls: "bg-yellow-100 text-yellow-700", Icon: PauseCircle },
    provision_failed: { label: "Provisioning Failed", cls: "bg-red-100 text-red-700", Icon: AlertTriangle },
  };
  const s = map[status] || { label: status || "Unknown", cls: "bg-gray-100 text-gray-700", Icon: AlertTriangle };
  const Icon = s.Icon;
  return (
    <span className={`text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-1 ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label.toUpperCase()}
    </span>
  );
}


function HostingCard({ h, onShowCreds, onRenew }) {
  const expiresAt = h.expires_at ? new Date(h.expires_at) : null;
  const now = new Date();
  const daysToExpiry = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
  // Show renew within 6 months of expiry, OR if already expired/suspended
  const showRenew = (daysToExpiry !== null && daysToExpiry <= 183) || h.status === "expired_suspended";
  const urgent = daysToExpiry !== null && daysToExpiry <= 30;

  return (
    <div className="lm-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 lm-icon-tile inline-flex items-center justify-center">
            <Globe className="w-5 h-5" />
          </span>
          <div>
            <h3 className="lm-font font-bold text-[#11151C] text-lg break-all">{h.domain_name}</h3>
            <p className="text-xs text-[#11151C]/55">{h.plan_name}</p>
          </div>
        </div>
        <StatusPill status={h.status} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[#11151C]/55 uppercase font-bold tracking-wider text-[10px]">Created</div>
          <div className="text-[#11151C] mt-0.5">{h.starts_at ? new Date(h.starts_at).toLocaleDateString() : "—"}</div>
        </div>
        <div>
          <div className="text-[#11151C]/55 uppercase font-bold tracking-wider text-[10px]">Expires</div>
          <div className={`mt-0.5 inline-flex items-center gap-1 ${urgent ? "text-red-600 font-semibold" : "text-[#11151C]"}`}>
            <Calendar className="w-3 h-3" />
            {h.expires_at ? new Date(h.expires_at).toLocaleDateString() : "—"}
            {daysToExpiry !== null && daysToExpiry > 0 && daysToExpiry <= 183 && (
              <span className={`ml-1 text-[10px] font-bold ${urgent ? "text-red-600" : "text-[#FF6B3D]"}`}>
                · {daysToExpiry}d left
              </span>
            )}
          </div>
        </div>
      </div>

      {showRenew && (
        <div className={`mt-4 p-3 rounded-xl border ${urgent ? "border-red-200 bg-red-50" : "border-[#FF6B3D]/30 bg-[#FFFBF6]"}`} data-testid={`renew-banner-${h.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              <div className={`font-bold ${urgent ? "text-red-700" : "text-[#B23B12]"}`}>
                {h.status === "expired_suspended"
                  ? "Hosting expired — renew to reactivate"
                  : daysToExpiry <= 0
                  ? "Expired"
                  : `Renew now to keep ${h.domain_name} live`}
              </div>
              <div className="text-[#11151C]/60 mt-0.5">Pick 1, 2 or 3 years for the best per-month rate.</div>
            </div>
            <button
              onClick={() => onRenew(h)}
              data-testid={`hosting-renew-${h.id}`}
              className="lm-btn-primary inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold"
            >
              <Calendar className="w-3.5 h-3.5" /> Renew
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-2">
        <button
          onClick={() => onShowCreds(h)}
          disabled={h.status !== "active"}
          data-testid={`hosting-show-creds-${h.id}`}
          title={h.status !== "active" ? "Account suspended — please contact support" : "View login credentials and control panel"}
          className="lm-btn-primary w-full py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <KeyRound className="w-4 h-4 text-[#FF6B3D]" />
          View Login Credentials
          <ExternalLink className="w-4 h-4" />
        </button>
        {h.has_support_credentials && (
        <button
          type="button"
          onClick={() => onShowCreds(h, "support")}
          disabled={h.status !== "active"}
          data-testid={`hosting-support-${h.id}`}
          title="View credentials to log in to the Support &amp; Tickets panel"
          className="w-full py-2.5 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-2 border border-[#FF6B3D]/40 bg-[#FFFBF6] text-[#B23B12] hover:bg-[#FFE6D6] disabled:opacity-60"
        >
          <Mail className="w-3.5 h-3.5" />
          Support &amp; Tickets
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
        )}
      </div>
    </div>
  );
}


function CopyButton({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* noop */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!value}
      data-testid={testid}
      title={`Copy ${label}`}
      className="ml-2 p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70 disabled:opacity-30"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}


function CredentialsModal({ cred, onClose, onChanged, onWelcome, welcomeLoading, status }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwErr, setPwErr] = useState("");

  const isSupport = cred.mode === "support";
  const targetUrl = isSupport ? cred.support_panel_url : cred.control_panel_url;
  const title = isSupport ? "Support & Tickets Login" : "Control Panel Login";
  const ctaLabel = isSupport ? "Open Support Panel" : "Open Login Page";
  // Control panel login uses USERNAME (= domain). Support login uses EMAIL (admin-entered).
  const identityLabel = isSupport ? "Email" : "Username";
  const identityValue = isSupport ? cred.support_email : (cred.username || cred.domain);
  const passwordValue = isSupport ? cred.support_password : cred.password;
  const hasPassword = isSupport ? !!cred.support_password : !!cred.has_password;

  const savePassword = async () => {
    setPwErr("");
    if (!newPw || newPw.length < 8) {
      setPwErr("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      const r = await customerChangeStackPassword(cred.hosting.id, newPw);
      if (!r.ok) {
        setPwErr(r.error || "Could not update password.");
      } else {
        setEditing(false);
        setNewPw("");
        setShow(true);
        await onChanged();
      }
    } catch (e) {
      setPwErr(e?.response?.data?.detail || "Could not update password.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="portal-creds-modal">
        <h3 className="lm-font font-extrabold text-[#11151C] text-xl flex items-center gap-2">
          {isSupport ? <Mail className="w-5 h-5 text-[#FF6B3D]" /> : <Server className="w-5 h-5 text-[#FF6B3D]" />}
          {title}
        </h3>
        <p className="text-sm text-[#11151C]/65 mt-1">{cred.hosting?.domain_name}</p>
        {isSupport && (
          <p className="text-[11px] text-[#11151C]/65 mt-1 italic">
            Use these credentials to access the support / ticket panel.
          </p>
        )}

        <div className="mt-4 p-4 rounded-xl bg-[#FFFBF6] border border-[#FF6B3D]/20 space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> {isSupport ? "Support URL" : "Login URL"}
            </div>
            <div className="mt-1 flex items-center justify-between">
              <a
                href={targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#FF6B3D] font-semibold break-all"
                data-testid="creds-url-link"
              >
                {targetUrl} <ExternalLink className="w-3 h-3" />
              </a>
              <CopyButton value={targetUrl} label="URL" testid="creds-copy-url" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> {identityLabel}
            </div>
            <div className="mt-1 flex items-center justify-between">
              <code className="font-mono text-sm text-[#11151C] break-all" data-testid="creds-username">{identityValue || "—"}</code>
              <CopyButton value={identityValue} label={identityLabel.toLowerCase()} testid="creds-copy-username" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
              <KeyRound className="w-3 h-3" /> Password
            </div>
            {hasPassword ? (
              <div className="mt-1 flex items-center justify-between">
                <code className="font-mono text-sm text-[#11151C] break-all" data-testid="creds-password">
                  {show ? passwordValue : "•".repeat(Math.max(8, (passwordValue || "").length))}
                </code>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    title={show ? "Hide" : "Reveal"}
                    data-testid="creds-toggle-show"
                    className="ml-2 p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70"
                  >
                    {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <CopyButton value={passwordValue} label="password" testid="creds-copy-password" />
                </div>
              </div>
            ) : (
              <p className="mt-1 text-xs text-[#11151C]/60 italic">
                {isSupport
                  ? "Support credentials not yet provided — please contact us."
                  : "No password saved yet — set one below or use the \"Email me login instructions\" option."}
              </p>
            )}
          </div>
        </div>

        {/* Change password — only for control-panel login (support pw is admin-managed) */}
        {!isSupport && (
        <div className="mt-5">
          {!editing ? (
            <button
              type="button"
              onClick={() => { setEditing(true); setPwErr(""); }}
              data-testid="creds-edit-password-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#FF6B3D]/40 bg-[#FFFBF6] text-[#B23B12] hover:bg-[#FFE6D6]"
            >
              <KeyRound className="w-4 h-4" />
              {hasPassword ? "Change Password" : "Set Password"}
            </button>
          ) : (
            <div className="border border-[#FF6B3D]/30 rounded-xl p-3 bg-[#FFFBF6]">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70">
                New password (8+ chars)
              </label>
              <input
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Enter a strong password"
                autoComplete="new-password"
                data-testid="creds-new-password-input"
                className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm font-mono"
              />
              {pwErr && (
                <p className="text-xs text-red-600 mt-1.5 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {pwErr}
                </p>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={savePassword}
                  disabled={saving}
                  data-testid="creds-save-password-btn"
                  className="lm-btn-primary flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setNewPw(""); setPwErr(""); }}
                  className="px-3 py-2 text-sm font-semibold text-[#11151C]"
                >
                  Cancel
                </button>
              </div>
              <p className="text-[11px] text-[#11151C]/55 mt-2">
                This updates your hosting control panel password instantly.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Primary action: open login page */}
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="creds-open-cp-btn"
          className="mt-4 lm-btn-primary w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold"
        >
          {ctaLabel} <ExternalLink className="w-4 h-4" />
        </a>

        {/* Forgot — email instructions (only for control panel, support pw is admin-managed) */}
        {!isSupport && (
        <div className="mt-4 pt-4 border-t border-[#11151C]/10">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 mb-2 inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Or get a fresh login link by email
          </div>
          <button
            onClick={onWelcome}
            disabled={welcomeLoading}
            data-testid="portal-send-welcome"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border border-[#11151C]/15 hover:bg-[#FFE6D6]/40 disabled:opacity-60"
          >
            {welcomeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Email me login instructions
          </button>
          {status?.msg && (
            <p className="text-xs text-green-600 mt-2 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {status.msg}
            </p>
          )}
          {status?.err && (
            <p className="text-xs text-red-600 mt-2 inline-flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {status.err}
            </p>
          )}
        </div>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[#11151C]" data-testid="creds-close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


export default function PortalPage() {
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [hosting, setHosting] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payLinks, setPayLinks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [cred, setCred] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [status, setStatus] = useState({ msg: "", err: "" });
  const [pageNotice, setPageNotice] = useState({ msg: "", err: "" });
  const [renewModal, setRenewModal] = useState(null); // { hosting, tiers, months, loading, submitting }

  const load = useCallback(async () => {
    try {
      const [h, i, pl] = await Promise.all([
        customerListHosting(),
        customerListInvoices(),
        customerPendingPayLinks().catch(() => []),
      ]);
      setHosting(h);
      setInvoices(i);
      setPayLinks(pl || []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role === "admin") {
      navigate("/admin");
      return;
    }
    load();
  }, [user, loading, navigate, load]);

  const openCreds = async (h, mode = "login") => {
    setStatus({ msg: "", err: "" });
    setPageNotice({ msg: "", err: "" });
    try {
      const info = await customerGetCredentials(h.id);
      setCred({ ...info, hosting: h, mode });
    } catch (e) {
      setPageNotice({ msg: "", err: e?.response?.data?.detail || "Could not load credentials." });
    }
  };

  const refreshCreds = async () => {
    if (!cred?.hosting?.id) return;
    try {
      const info = await customerGetCredentials(cred.hosting.id);
      setCred((prev) => ({ ...prev, ...info }));
    } catch (e) { /* keep modal open */ }
  };

  const sendWelcome = async () => {
    if (!cred?.hosting?.id) return;
    setWelcomeLoading(true);
    try {
      const r = await customerSendWelcome(cred.hosting.id);
      setStatus({ msg: `Login link sent to ${r.sent_to}. Check your email (and spam folder).`, err: "" });
    } catch (e) {
      setStatus({ msg: "", err: e?.response?.data?.detail || "Could not send welcome email" });
    }
    setWelcomeLoading(false);
  };

  const openRenew = async (h) => {
    setRenewModal({ hosting: h, tiers: [], months: 12, loading: true, submitting: false });
    try {
      const opts = await customerRenewOptions(h.id);
      const tiers = (opts.tiers && opts.tiers.length) ? opts.tiers : [{ months: 12, price: h.amount_inr || 0 }];
      setRenewModal({ hosting: h, tiers, months: Number(tiers[0].months) || 12, loading: false, submitting: false });
    } catch (e) {
      setRenewModal(null);
      setPageNotice({ msg: "", err: e?.response?.data?.detail || "Could not load renewal options" });
    }
  };

  // Load Razorpay checkout SDK on portal mount so renewals can launch instantly.
  useEffect(() => {
    if (document.querySelector('script[data-rzp-sdk="1"]')) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.setAttribute("data-rzp-sdk", "1");
    document.body.appendChild(s);
  }, []);

  const submitRenewal = async () => {
    if (!renewModal?.hosting?.id) return;
    setRenewModal((m) => ({ ...m, submitting: true }));
    const months = Number(renewModal.months);
    const hostingId = renewModal.hosting.id;
    const domain = renewModal.hosting.domain_name;
    try {
      const data = await customerRenewCreateOrder(hostingId, months);
      // Fallback when Razorpay isn't configured: create an unpaid invoice + ask admin to follow up
      if (!data.razorpay_configured || !data.razorpay_order || !window.Razorpay) {
        const r = await customerRequestRenewal(hostingId, months);
        setRenewModal(null);
        setPageNotice({
          msg: `Renewal request received (${r.invoice_no}). Our team will reach out shortly to complete payment.`,
          err: "",
        });
        return;
      }
      const opts = {
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Live Mover",
        description: `Renew ${domain} (${months / 12} year${months / 12 > 1 ? "s" : ""})`,
        order_id: data.razorpay_order.id,
        prefill: { email: renewModal.hosting.customer_email },
        theme: { color: "#FF6B3D" },
        handler: async (resp) => {
          try {
            const v = await customerRenewVerify(hostingId, {
              order_id: data.order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            try {
              trackPurchase({
                transaction_id: resp.razorpay_payment_id,
                plan_key: renewModal.hosting.plan_key,
                plan_name: `${renewModal.hosting.plan_name} (renewal)`,
                value: Number(data.total_inr) || Number(data.amount_inr) || 0,
                currency: "INR",
                months,
              });
            } catch (_) { /* ignore */ }
            setRenewModal(null);
            setPageNotice({
              msg: `${domain} renewed — new expiry: ${new Date(v.new_expires_at).toLocaleDateString()}. Receipt ${v.invoice_no} sent to your email.`,
              err: "",
            });
            await load();
          } catch (e) {
            setRenewModal(null);
            setPageNotice({ msg: "", err: e?.response?.data?.detail || "Renewal verification failed. Contact support with your payment id." });
          }
        },
        modal: {
          ondismiss: () => setRenewModal((m) => (m ? { ...m, submitting: false } : null)),
        },
      };
      try {
        trackBeginCheckout({
          plan_key: renewModal.hosting.plan_key,
          plan_name: `${renewModal.hosting.plan_name} (renewal)`,
          price: data.amount_inr,
          value: Number(data.total_inr) || Number(data.amount_inr) || 0,
          currency: "INR",
          months,
        });
      } catch (_) { /* ignore */ }
      const rzp = new window.Razorpay(opts);
      rzp.open();
    } catch (e) {
      setRenewModal((m) => ({ ...m, submitting: false }));
      setPageNotice({ msg: "", err: e?.response?.data?.detail || "Could not open Razorpay" });
    }
  };

  const [payingInvoice, setPayingInvoice] = useState(null); // invoice_id while in flight

  const payInvoice = async (inv) => {
    setPayingInvoice(inv.id);
    setPageNotice({ msg: "", err: "" });
    try {
      const data = await customerInvoicePayCreateOrder(inv.id);
      if (!window.Razorpay) {
        setPageNotice({ msg: "", err: "Payment widget didn't load. Refresh and try again." });
        return;
      }
      const opts = {
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Live Mover",
        description: data.description || `Invoice ${data.invoice_no}`,
        order_id: data.razorpay_order_id,
        prefill: { name: data.customer_name, email: data.customer_email },
        theme: { color: "#FF6B3D" },
        handler: async (resp) => {
          try {
            const v = await customerInvoicePayVerify(inv.id, {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            try {
              trackPurchase({
                transaction_id: resp.razorpay_payment_id,
                plan_key: inv.type || "invoice",
                plan_name: data.description || `Invoice ${data.invoice_no}`,
                value: Number(data.total_inr) || Number(data.amount_inr) || 0,
                currency: data.currency || "INR",
                months: 12,
              });
            } catch (_) { /* ignore */ }
            const renewedNote = v.new_expires_at
              ? ` New expiry: ${new Date(v.new_expires_at).toLocaleDateString()}.`
              : "";
            setPageNotice({ msg: `Invoice ${v.invoice_no} paid.${renewedNote}`, err: "" });
            await load();
          } catch (e) {
            setPageNotice({ msg: "", err: e?.response?.data?.detail || "Payment verification failed. Contact support with your payment id." });
          } finally {
            setPayingInvoice(null);
          }
        },
        modal: {
          ondismiss: () => setPayingInvoice(null),
        },
      };
      try {
        trackBeginCheckout({
          plan_key: inv.type || "invoice",
          plan_name: data.description || `Invoice ${data.invoice_no}`,
          price: data.amount_inr,
          value: Number(data.total_inr) || Number(data.amount_inr) || 0,
          currency: data.currency || "INR",
          months: 12,
        });
      } catch (_) { /* ignore */ }
      new window.Razorpay(opts).open();
    } catch (e) {
      setPayingInvoice(null);
      setPageNotice({ msg: "", err: e?.response?.data?.detail || "Could not open Razorpay" });
    }
  };

  if (loading || busy) {
    return (
      <div className="lm-font min-h-screen flex flex-col items-center justify-center bg-[#FFFBF6]">
        <Logo size={48} />
        <p className="mt-4 text-[#11151C]/60">Loading your account...</p>
      </div>
    );
  }

  return (
    <div className="lm-font min-h-screen bg-[#FAF8F4]">
      <div className="bg-white border-b border-[#11151C]/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} />
            <span className="lm-font font-extrabold text-[#11151C]">Live Mover</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#11151C]/60">{user?.email}</span>
            <button
              onClick={() => { signOut(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#11151C] hover:text-red-600"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="lm-font font-extrabold text-[#11151C] text-3xl sm:text-4xl">
          Welcome{user?.name ? `, ${user.name}` : ""}
        </h1>
        <p className="text-[#11151C]/65 mt-2">Manage your hosting, view invoices, and access your control panel.</p>

        {user?.support_pin && (
          <div className="mt-5 inline-flex items-center gap-3 bg-[#FFFBF6] border border-[#FF6B3D]/30 rounded-xl px-4 py-3" data-testid="support-pin-card">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/55">Your 6-digit support PIN</div>
              <code className="lm-font font-extrabold text-2xl text-[#B23B12] tracking-[0.35em]">{user.support_pin}</code>
            </div>
            <div className="text-[11px] text-[#11151C]/60 max-w-[240px] leading-snug">
              Share this PIN if our support team asks you to verify your account during live chat.
            </div>
          </div>
        )}

        {pageNotice.err && (
          <div className="mt-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="portal-page-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{pageNotice.err}</span>
          </div>
        )}

        <div className="mt-8">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="lm-section-title text-xl inline-flex items-center gap-2">
              <Server className="w-5 h-5 text-[#FF6B3D]" /> My Hosting ({hosting.length})
            </h2>
            <Link
              to="/#pricing"
              data-testid="portal-buy-another"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FFE6D6] text-[#B23B12] text-sm font-bold hover:brightness-95"
            >
              <Globe className="w-4 h-4" /> Buy another hosting
            </Link>
          </div>
          {hosting.length === 0 ? (
            <div className="lm-card p-8 text-center">
              <Globe className="w-10 h-10 text-[#FF6B3D] mx-auto" />
              <h3 className="lm-font font-bold text-[#11151C] mt-3">No hosting accounts yet</h3>
              <p className="text-[#11151C]/60 text-sm mt-1">Pick a plan to get started.</p>
              <Link to="/#pricing" className="lm-btn-primary mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold">
                View Pricing
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {hosting.map((h) => (
                <HostingCard
                  key={h.id}
                  h={h}
                  onShowCreds={openCreds}
                  onRenew={openRenew}
                />
              ))}
            </div>
          )}
        </div>

        {payLinks.length > 0 && (
          <div className="mt-10" data-testid="portal-pending-paylinks">
            <h2 className="lm-section-title text-xl mb-4 inline-flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#FF6B3D]" /> Pending payments ({payLinks.length})
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {payLinks.map((p) => {
                const exp = p.expires_at ? new Date(p.expires_at) : null;
                const expired = exp && exp < new Date();
                return (
                  <div
                    key={p.token}
                    className={`lm-card p-4 border ${expired ? "border-red-200 bg-red-50/40" : "border-[#FF6B3D]/25 bg-[#FFFBF6]"}`}
                    data-testid={`portal-paylink-${p.token}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-[#11151C]">{p.plan_name}</div>
                        <div className="text-xs text-[#11151C]/60 break-all">{p.domain_name}</div>
                        {p.locked_renewal_price ? (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B23B12] bg-white px-1.5 py-0.5 rounded">
                            Locks renewal at ₹{p.locked_renewal_price}
                          </div>
                        ) : null}
                      </div>
                      <div className="text-right">
                        <div className="font-extrabold text-[#11151C]">₹{Number(p.total_inr || 0).toLocaleString("en-IN")}</div>
                        <div className="text-[11px] text-[#11151C]/55">incl. GST</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-[#11151C]/55">
                        {expired
                          ? "Link expired — open it to get a fresh one"
                          : (exp ? `Expires ${exp.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : "")}
                      </div>
                      <Link
                        to={`/checkout?token=${p.token}`}
                        data-testid={`portal-paynow-${p.token}`}
                        className="lm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
                      >
                        Pay now <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12">
          <h2 className="lm-section-title text-xl mb-4 inline-flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#FF6B3D]" /> My Invoices ({invoices.length})
          </h2>
          {invoices.length === 0 ? (
            <div className="lm-card p-6 text-center text-[#11151C]/60 text-sm">No invoices yet.</div>
          ) : (
            <div className="lm-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FFFBF6]">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-left p-3">Amount</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-t border-[#11151C]/5" data-testid={`invoice-row-${i.id}`}>
                      <td className="p-3 text-xs">{new Date(i.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-xs">
                        {(i.items || []).map((it, idx) => (<div key={idx}>{it.description}</div>))}
                      </td>
                      <td className="p-3 font-bold">₹{i.total_inr?.toLocaleString("en-IN")}</td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          i.status === "paid" ? "bg-green-100 text-green-700" :
                          i.status === "unpaid" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"
                        }`}>{i.status}</span>
                      </td>
                      <td className="p-3 text-right">
                        {i.status === "unpaid" ? (
                          <button
                            onClick={() => payInvoice(i)}
                            disabled={payingInvoice === i.id}
                            data-testid={`invoice-paynow-${i.id}`}
                            className="lm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold disabled:opacity-60"
                          >
                            {payingInvoice === i.id ? "Opening…" : "Pay now →"}
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#11151C]/40">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {cred && (
        <CredentialsModal
          cred={cred}
          onClose={() => setCred(null)}
          onChanged={refreshCreds}
          onWelcome={sendWelcome}
          welcomeLoading={welcomeLoading}
          status={status}
        />
      )}

      {renewModal && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setRenewModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="renew-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg inline-flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FF6B3D]" /> Renew hosting
              </h3>
              <button onClick={() => setRenewModal(null)} className="text-[#11151C]/50"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65">{renewModal.hosting?.plan_name} · <span className="font-semibold text-[#11151C]">{renewModal.hosting?.domain_name}</span></p>

            {renewModal.loading ? (
              <div className="py-8 text-center text-[#11151C]/55 text-sm">Loading options…</div>
            ) : (
              <>
                <div className="mt-4 space-y-2">
                  {renewModal.tiers.map((t) => {
                    const m = Number(t.months);
                    const firstYear = Number(t.price) || 0;
                    const price = Number(t.renewal_price_effective ?? t.renewal_price ?? t.price) || 0;
                    const per = m > 0 ? (price / m).toFixed(2) : "0";
                    const active = Number(renewModal.months) === m;
                    const isLocked = t.renewal_price_source === "locked";
                    const isDiscounted = price < firstYear;
                    return (
                      <label
                        key={m}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl cursor-pointer border ${active ? "border-[#FF6B3D] bg-[#FFFBF6]" : "border-[#11151C]/10 hover:border-[#FF6B3D]/40"}`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={active}
                            onChange={() => setRenewModal((mod) => ({ ...mod, months: m }))}
                            data-testid={`renew-tier-${m}`}
                            className="accent-[#FF6B3D]"
                          />
                          <span className="font-semibold text-[#11151C] text-sm">{m === 12 ? "1 year" : `${m / 12} years`}</span>
                          {isLocked && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#B23B12] bg-[#FFE6D6] px-1.5 py-0.5 rounded">Special</span>
                          )}
                        </div>
                        <div className="text-right">
                          {isDiscounted && (
                            <div className="text-[10px] text-[#11151C]/45 line-through leading-none">₹{firstYear.toLocaleString("en-IN")}</div>
                          )}
                          <div className="font-bold text-[#11151C] text-sm">₹{price.toLocaleString("en-IN")}</div>
                          <div className="text-[11px] text-[#11151C]/55">₹{Number(per).toLocaleString("en-IN", { maximumFractionDigits: 2 })} a month</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-[#11151C]/55">
                  Submitting a renewal request creates an unpaid invoice. Our team will reach out shortly with payment instructions. After payment your hosting is extended automatically.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => setRenewModal(null)} className="px-4 py-2 text-sm font-semibold text-[#11151C]">Cancel</button>
                  <button
                    onClick={submitRenewal}
                    disabled={renewModal.submitting}
                    data-testid="renew-submit"
                    className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-60"
                  >
                    {renewModal.submitting ? "Submitting…" : "Submit renewal request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
